import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getBaseUrl } from "../config";
import { getAuthApiHeaders } from "../../utils/common";
import secureLocalStorage from "react-secure-storage";

const baseUrl = getBaseUrl();

/** SSE parser (now supports final + clarification) */
const parseSseBuffer = (buffer) => {
  const parts = buffer.split("\n\n");
  const remainder = parts.pop();
  const chunks = [];
  let finalEvent = null;
  let clarificationEvent = null;
  let dataEvent = null;

  for (const raw of parts) {
    const lines = raw.split("\n").map((l) => l.replace(/\r$/, ""));
    const dataLines = lines
      .filter((l) => l.startsWith("data:"))
      .map((l) => l.slice(5).trimStart());

    if (!dataLines.length) continue;

    const joined = dataLines.join("\n");

    if (joined.startsWith("{") && joined.endsWith("}")) {
      try {
        const obj = JSON.parse(joined);

        if (obj && obj.event === "final") {
          finalEvent = obj;
          continue;
        }

        if (obj && obj.event === "clarification") {
          clarificationEvent = obj;
          continue;
        }

        // Incremental prose, emitted before the terminal `final` event.
        // `token` is accepted as an alias so either backend name works.
        if (obj && (obj.event === "answer_text" || obj.event === "token") && obj.content) {
          chunks.push(obj.content);
          continue;
        }

        // Tables and charts, which arrive after the prose.
        if (obj && obj.event === "answer_data") {
          dataEvent = obj;
          continue;
        }

        // Any other structured event is metadata, not prose. Without this
        // guard it would fall through below and render as raw JSON inside
        // the typewriter.
        if (obj && obj.event) continue;
      } catch (e) {
        // treat as text
      }
    }

    for (const d of dataLines) chunks.push(d);
  }

  return { remainder, chunks, finalEvent, clarificationEvent, dataEvent };
};

export const postChatStream = createAsyncThunk(
  "data/postChatStream",
  async (payload, { dispatch }) => {
    const headers = {
      "Content-Type": "application/json",
      "use-case": "src",
      ...getAuthApiHeaders({ includeIncomingEmail: true }),
      client: payload.client ? payload.client : "UT",
    };

    try {
      const endpoint = payload.endpoint || "chat";

      const response = await fetch(`${baseUrl}/${endpoint}`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload.data),
        credentials: "include",
      });

      if (!response.ok) {
        console.error("Network response was not ok:", response.statusText);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const {
          remainder,
          chunks,
          finalEvent,
          clarificationEvent,
          dataEvent,
        } = parseSseBuffer(buffer);
        buffer = remainder;

        if (chunks.length) {
          for (const c of chunks) dispatch(addItem(c));
        }

        if (dataEvent) {
          dispatch(setDataEvent(dataEvent));
        }

        if (clarificationEvent) {
          dispatch(setClarificationEvent(clarificationEvent));
        }

        if (finalEvent) {
          dispatch(setFinalEvent(finalEvent));
        }
      }
    } catch (error) {
      console.error("Fetch error:", error?.message || error);
    }
  },
);

export const getSessionChats = createAsyncThunk(
  "data/getSessionChats",
  async (payload, { rejectWithValue }) => {
    const headers = {
      ...getAuthApiHeaders({
        includeUserEmail: false,
        includeBusinessFunction: false,
        includeIncomingEmail: true,
      }),
      client: payload.client ? payload.client : "UT",
    };

    const offset = payload.offset ?? 0;
    const limit = payload.limit ?? 200;

    try {
      const res = await fetch(
        `${baseUrl}/sessions/${payload.sessionId}/chats?offset=${offset}&limit=${limit}`,
        { method: "GET", headers, credentials: "include" },
      );

      if (!res.ok) {
        return rejectWithValue(res.statusText);
      }

      return await res.json();
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to fetch session chats");
    }
  },
);

const dataSlice = createSlice({
  name: "data",
  initialState: {
    items: [],
    loading: false,
    streamEnd: false,
    finalEvent: null,
    clarificationEvent: null,
    dataEvent: null,
    history: null,
  },
  reducers: {
    // Tables/chart payload that arrives after the prose. Held separately
    // from finalEvent so the UI can render a table as soon as it lands,
    // rather than waiting for the terminal event.
    setDataEvent(state, action) {
      state.dataEvent = action.payload;
    },
    addItem(state, action) {
      state.items.push(action.payload);
    },
    clearStreamingData(state, action) {
      state.dataEvent = null;
      state.streamEnd = action.payload;
      if (action.payload === false) {
        state.items = [];
        state.finalEvent = null;
        state.clarificationEvent = null;
      }
    },
    setFinalEvent(state, action) {
      state.finalEvent = action.payload;
    },
    setClarificationEvent(state, action) {
      state.clarificationEvent = action.payload;
    },
    clearHistory(state) {
      state.history = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postChatStream.pending, (state) => {
        state.loading = true;
        state.items = [];
        state.finalEvent = null;
        state.clarificationEvent = null;
      })
      .addCase(postChatStream.fulfilled, (state) => {
        state.loading = false;
        state.streamEnd = true;
      })
      .addCase(postChatStream.rejected, (state) => {
        state.loading = false;
      })

      .addCase(getSessionChats.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSessionChats.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload;
      })
      .addCase(getSessionChats.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  addItem,
  clearStreamingData,
  setFinalEvent,
  setClarificationEvent,
  setDataEvent,
  clearHistory,
} = dataSlice.actions;

export default dataSlice.reducer;
